import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useSelector } from 'react-redux';
import { getIpLocale, getOrgContact, getWeatherForecast } from 'api/account';
import dayjs from 'dayjs';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import './weather-widget.scss';
import { Loading } from 'components/Loading';
import { APTooltipProvider } from 'components/Tooltip';
import clsx from 'clsx';

dayjs.extend(customParseFormat);

const WeatherWidgetInner = () => {
  const isMobileDashboard = useMediaQuery({ maxWidth: 767 });
  const orgId = useSelector(state => state.global.userInfo.orgId);
  const [weather, setWeather] = useState(null);
  const [location, setLocation] = useState(null);
  const locationRef = useRef(null);
  const [weatherForecast, setWeatherForecast] = useState(null);
  const [isForecastVisible, setForecastVisible] = useState(false);

  useEffect(() => {
    // ipapi is rate limited, so fall back to the organization's contact address.
    const resolveLocation = async () => {
      const ipResponse = await getIpLocale();
      if (ipResponse?.data?.ip) {
        return {
          city: ipResponse.data.city,
          country: ipResponse.data.country_code_iso3,
          params: { ip: ipResponse.data.ip },
        };
      }

      if (!orgId) return null;
      const contactResponse = await getOrgContact(orgId);
      const address = contactResponse?.data?.addresses?.[0];
      if (!address?.city) return null;

      return {
        city: address.city,
        country: address.country,
        params: { city: address.city, zip: address.postcode },
      };
    };

    const fetchWeather = async () => {
      if (!locationRef.current) {
        const resolved = await resolveLocation();
        if (!resolved) return;
        locationRef.current = resolved;
        setLocation(resolved);
      }

      const weatherResponse = await getWeatherForecast(locationRef.current.params);

      setWeather(prev => {
        return weatherResponse?.data?.current || prev;
      });
      setWeatherForecast(prev => {
        return weatherResponse?.data?.forecast?.forecastday || prev;
      });
    };
    fetchWeather();
    // Check weather every minute
    const interval = setInterval(fetchWeather, 60000);
    return () => clearInterval(interval);
  }, [orgId]);

  if (!weather || !weatherForecast || !location) {
    return (
      <div className={clsx('weather-wrapper', isMobileDashboard && 'weather-wrapper--mobile-compact')}>
        <div className={clsx('weather', isMobileDashboard && 'weather--mobile-compact')}>
          <Loading />
        </div>
      </div>
    );
  }

  if (isMobileDashboard) {
    return (
      <div className="weather-wrapper weather-wrapper--mobile-compact">
        <div className="weather weather--mobile-compact">
          <div className="weather__mobile-compact-inner">
            {weather?.condition?.icon ? (
              <>
                <img src={weather.condition.icon}
                  alt=""
                />
                <div className="weather__temp-mobile">{Math.round(weather.temp_c)}°</div>
              </>
            ) : (
              <Loading />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('weather-wrapper', isForecastVisible && 'expanded')}>
      <div className="weather">
        <div
          className="weather__image"
          onClick={() => setForecastVisible(prev => !prev)}
        >
          {
            weather?.condition?.icon ?
              (
                <>
                  <img src={weather.condition.icon} />
                  <div className="weather__condition">{weather.condition.text}</div>
                </>
              ) :
                <Loading />
          }
        </div>
        <div className="vr" />
        <div className="weather__content">
          <div className="weather__data">
            <div className="weather__day">
              {weatherForecast[0] ? dayjs(weatherForecast[0].date).format('ddd, MMM DD') : ''}
            </div>
            <div className="weather__city">{location.city}, {location.country}</div>
            <div className="weather__temp">
              {weather.temp_c}° C / {weather.temp_f}° F
            </div>
          </div>
        </div>
      </div>
      {isForecastVisible && (
        <div className="weather-list">
          {weatherForecast.map((forecast, index) => {
            if (index === 0) return null;
            return (
              <>
                <div
                  className="weather"
                  key={`forecast-${index}`}
                >
                  <div className="weather__image">
                    <img src={forecast.day.condition.icon} />
                    <div className="weather__condition">{forecast.day.condition.text}</div>
                  </div>
                  <div className="vr" />
                  <div className="weather__content">
                    <div className="weather__data">
                      <div className="weather__day">{dayjs(forecast.date).format('ddd, MMM DD')}</div>
                      <div className="weather__city">
                        {location.city}, {location.country}
                      </div>
                      <div className="weather__temp">
                        {forecast.day.avgtemp_c}° C / {forecast.day.avgtemp_f}° F
                      </div>
                    </div>
                  </div>
                </div>
              </>
            );
          })}
        </div>
      )}
    </div>
  );
};

const WeatherWidget = (props) => (
  <APTooltipProvider
    tooltipDelay={300}
    floatingProps={{ placement: 'top' }}
  >
    <WeatherWidgetInner {...props} />
  </APTooltipProvider>
);

export default WeatherWidget;
